import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { 
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemContent,
  Typography,
  Divider,
  Box
} from '@mui/joy';
import { formatTitle } from '../../helpers/policyUtils';

const NavigationBlocker = ({ whenToBlock, changesToList }) => {
  const [showModal, setShowModal] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const shouldBlock = whenToBlock && !confirmedNavigation && currentLocation.pathname !== nextLocation.pathname;
    return shouldBlock;
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (whenToBlock) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [whenToBlock]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowModal(true);
    }
  }, [blocker.state]);

  const handleCancel = () => {
    blocker.reset();
    setShowModal(false);
  };

  const handleConfirm = () => {
    setConfirmedNavigation(true);
    blocker.proceed();
  };

  const renderChanges = () => {
    if (!changesToList) return null;

    const sections = [];
    
    if (changesToList.name) {
      sections.push(
        <Box key="name">
          <Typography level="title-md">Policy Name</Typography>
          <List>
            <ListItem>
              <ListItemContent>
                <Typography level="body-sm">
                  Name: {changesToList.name}
                </Typography>
              </ListItemContent>
            </ListItem>
          </List>
        </Box>
      );
    }
    
    if (Object.keys(changesToList.policy || {}).length > 0) {
      sections.push(
        <Box key="policy">
          <Typography level="title-md">Policy Changes</Typography>
          <List>
            {Object.entries(changesToList.policy).map(([key, value]) => (
              <ListItem key={key}>
                <ListItemContent>
                  <Typography level="body-sm">
                    {formatTitle(key)}: {typeof value === 'object' ? 'Modified' : value}
                  </Typography>
                </ListItemContent>
              </ListItem>
            ))}
          </List>
        </Box>
      );
    }

    if (Object.keys(changesToList.settings || {}).length > 0) {
      sections.push(
        <Box key="settings">
          <Typography level="title-md">Settings Changes</Typography>
          <List>
            {Object.entries(changesToList.settings).map(([key, value]) => (
              <ListItem key={key}>
                <ListItemContent>
                  <Typography level="body-sm">
                    {formatTitle(key)}: {typeof value === 'object' ? 'Modified' : value}
                  </Typography>
                </ListItemContent>
              </ListItem>
            ))}
          </List>
        </Box>
      );
    }

    if (changesToList.users && changesToList.users.length > 0) {
      sections.push(
        <Box key="users">
          <Typography level="title-md">User Assignments</Typography>
          <List>
            <ListItem>
              <ListItemContent>
                <Typography level="body-sm">
                  Assigned Users: {changesToList.users.length} user(s) selected
                </Typography>
              </ListItemContent>
            </ListItem>
          </List>
        </Box>
      );
    }

    return sections;
  };

  return (
    <Modal open={showModal} onClose={handleCancel}>
      <ModalDialog sx={{ maxWidth: 500 }}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography level="body-md" sx={{ mb: 2 }}>
            You have the following unsaved changes:
          </Typography>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {renderChanges()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancel}>
            Continue Editing
          </Button>
          <Button color="danger" onClick={handleConfirm}>
            Leave Without Saving
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default NavigationBlocker;